
import React from "react";
import { useParams, Link } from "react-router-dom";
import {Box, Container, Typography, Breadcrumbs, Link as MuiLink, Paper, Button, Stack, Chip} from "@mui/material";
import { Sparkles, ArrowLeft, Rocket, Download, Handshake, Monitor, Users, Cloud} from "lucide-react";
import PageShell from "../components/layout/PageShell";
import { getOverviewItemBySlug } from "../data/businessOverview";

export default function OverviewDetailPage() {
  const { slug } = useParams();
  const item = getOverviewItemBySlug(slug);

  return (
    <PageShell>
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #071A38 0%, #123B75 55%, #245CC5 100%)",
          color: "#fff",
          pt: { xs: 4, md: 5 },
          pb: { xs: 12, md: 16 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            separator="/"
            sx={{
              mb: 3,
              color: "#D5E4FF",
              "& .MuiBreadcrumbs-separator": { color: "#91B6F5" },
            }}
          >
            <MuiLink
              component={Link}
              to="/"
              underline="none"
              sx={{ color: "inherit", fontSize: 13 }}
            >
              Home
            </MuiLink>

            <MuiLink
              component={Link}
              to="/business?tab=overview"
              underline="none"
              sx={{ color: "inherit", fontSize: 13 }}
            >
              Business Overview
            </MuiLink>

            <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
              {item?.title || "Business Scope"}
            </Typography>
          </Breadcrumbs>

          {item ? (
            <>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
              >
                <Chip
                  icon={<Handshake size={15} />}
                  label="BUSINESS SERVICES"
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.17)",
                    fontWeight: 700,
                  }}
                />
                <Chip
                  icon={<Sparkles size={15} />}
                  label="PROFESSIONAL"
                  sx={{
                    color: "#10264A",
                    bgcolor: "#fff",
                    fontWeight: 700,
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  fontSize: { xs: "2.3rem", md: "3.8rem" },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 1.5,
                }}
              >
                {item.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  color: "#DCE9FF",
                  maxWidth: 750,
                }}
              >
                IT solutions, consulting and enterprise services
              </Typography>
            </>
          ) : (
            <Typography variant="h5">Page not found.</Typography>
          )}
        </Container>
      </Box>

      {item && (
        <Container
          maxWidth="lg"
          sx={{ mt: { xs: -7, md: -10 }, pb: 8, position: "relative" }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1.8fr) minmax(300px, 1fr)",
              },
              gap: { xs: 4, md: 5 },
              alignItems: "start",
            }}
          >
            <Box>
              <Paper
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: "24px",
                  border: "4px solid #fff",
                  boxShadow: "0 20px 45px rgba(8, 25, 55, 0.18)",
                  bgcolor: "#fff",
                }}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt={item.title}
                  sx={{
                    display: "block",
                    width: "100%",
                    height: { xs: 250, sm: 340, md: 420 },
                    objectFit: "cover",
                  }}
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 4,
                  p: { xs: 2.5, md: 4 },
                  borderRadius: "20px",
                  border: "1px solid #E1E8F2",
                  bgcolor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 25, md: 32 },
                    fontWeight: 800,
                    color: "#0B1D3A",
                    mb: 2,
                  }}
                >
                  {item.title}
                </Typography>

                {item.description && (
                  <Typography
                    sx={{
                      fontSize: 15.5,
                      color: "#475569",
                      lineHeight: 1.9,
                      mb: 2.5,
                      textAlign: "justify",
                    }}
                  >
                    {item.description}
                  </Typography>
                )}

                {item.detailed_description && (
                  <Typography
                    sx={{
                      fontSize: 14.5,
                    color: "#334155",
                    lineHeight: 1.9,
                    whiteSpace: "pre-line",
                    textAlign: "justify",
                    }}
                  >
                    {item.detailed_description}
                  </Typography>
                )}

                <MuiLink
                  component={Link}
                  to="/business?tab=overview"
                  underline="none"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 4,
                    fontWeight: 700,
                    color: "#245CC5",
                  }}
                >
                  <ArrowLeft size={17} />
                  Back to overview
                </MuiLink>
              </Paper>
            </Box>

            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  p: { xs: 3, md: 3.5 },
                  borderRadius: "26px",
                  color: "#fff",
                  background:
                    "linear-gradient(145deg, #173F91 0%, #2865E8 100%)",
                  boxShadow: "0 18px 35px rgba(25, 70, 155, 0.25)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: 130,
                    height: 130,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.13)",
                    top: -65,
                    right: -35,
                  },
                }}
              >
                <Rocket size={30} />

                <Typography
                  sx={{ fontSize: 25, fontWeight: 800, mt: 2, mb: 1 }}
                >
                  Let's work together
                </Typography>

                <Typography
                  sx={{
                    color: "#E3ECFF",
                    fontSize: 14,
                    lineHeight: 1.7,
                    mb: 3,
                  }}
                >
                  Looking for the right business or IT solution? Connect with
                  our team to discuss your requirements.
                </Typography>

                <Button
                  component={Link}
                  to="/business?tab=overview"
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.5,
                    borderRadius: "13px",
                    bgcolor: "#fff",
                    color: "#10264A",
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#EAF1FF" },
                  }}
                >
                  Explore other services
                </Button>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "22px",
                  bgcolor: "#fff",
                  border: "1px solid #E1E8F0",
                  boxShadow: "0 12px 30px rgba(15, 35, 65, 0.06)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 19,
                    fontWeight: 800,
                    color: "#0B1D3A",
                    mb: 2.5,
                  }}
                >
                  Our capabilities
                </Typography>

                {[
                  { icon: Monitor, label: "IT Services" },
                  { icon: Users, label: "Business Consultancy" },
                  { icon: Cloud, label: "Enterprise Solutions" },
                  { icon: Handshake, label: "Business Partnerships" },
                ].map(({ icon: Icon, label }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1.5,
                      borderBottom: "1px solid #EDF1F7",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: "12px",
                        bgcolor: "#EDF4FF",
                        color: "#245CC5",
                      }}
                    >
                      <Icon size={20} />
                    </Box>

                    <Typography
                      sx={{ fontWeight: 600, color: "#243B5A", fontSize: 14 }}
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Stack>
          </Box>
        </Container>
      )}
    </PageShell>
  );
}
